/**
 * ProjectionCard Component
 * Individual projection display card with key metrics and admin actions
 */

import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Box,
  Chip,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Divider
} from '@mui/material';
import {
  Edit,
  Delete,
  Info,
  CalendarToday,
  Person,
  AttachMoney
} from '@mui/icons-material';
import { formatCurrency, formatProjectionDate, getProjectionConfidence } from '../../types/boxOfficeTypes';

const ProjectionCard = ({ 
  projection, 
  filmId, 
  filmTitle, 
  canManage = false,
  onRefresh,
  onEditProjection
}) => {
  const [showDetails, setShowDetails] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Calculate confidence level
  const confidence = getProjectionConfidence(
    projection.actualProjection,
    projection.outlierMinimum,
    projection.outlierCeiling
  );

  // Get confidence color and label
  const getConfidenceInfo = () => {
    switch (confidence) {
      case 'high':
        return { color: 'success', label: 'High Confidence' };
      case 'medium':
        return { color: 'warning', label: 'Medium Confidence' };
      case 'low':
        return { color: 'error', label: 'Low Confidence' };
      default:
        return { color: 'default', label: 'Unknown' };
    }
  };

  const confidenceInfo = getConfidenceInfo();

  // Handle delete projection
  const handleDelete = async () => {
    // Check admin permissions
    if (!canManage) {
      console.error('User does not have admin permissions to delete projection');
      return;
    }

    setDeleting(true);
    try {
      console.log('Deleting projection:', projection.id);
      
      // Import API service for delete operation
      const { default: apiService } = await import('../../lib/services/apiService');
      await apiService.deleteBoxOfficeProjection(projection.id);
      
      console.log('Projection deleted successfully');
      setShowDeleteDialog(false);
      
      // Refresh the data
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Failed to delete projection:', error);
      // You could add error state handling here
    } finally {
      setDeleting(false);
    }
  };

  // Handle edit projection
  const handleEdit = () => {
    if (onEditProjection) {
      onEditProjection(projection);
    }
  };

  return (
    <>
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ flexGrow: 1 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                {formatCurrency(projection.actualProjection)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Actual Projection
              </Typography>
            </Box>
            <Chip
              label={confidenceInfo.label}
              size="small"
              color={confidenceInfo.color}
              variant="outlined"
            />
          </Box>

          {/* Key Metrics */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Projection Range:
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {formatCurrency(projection.projectionFloor)} - {formatCurrency(projection.projectionCeiling)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Outlier Range:
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {formatCurrency(projection.outlierMinimum)} - {formatCurrency(projection.outlierCeiling)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Days to Release:
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {projection.daysUntilRelease} days
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 1 }} />

          {/* Metadata */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <CalendarToday sx={{ fontSize: '1rem', color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">
              {formatProjectionDate(projection.projectionDate)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Person sx={{ fontSize: '1rem', color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">
              {projection.createdBy}
            </Typography>
          </Box>

          {/* Notes Preview */}
          {projection.notes && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Notes:
              </Typography>
              <Typography variant="body2" sx={{ 
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}>
                {projection.notes}
              </Typography>
            </Box>
          )}
        </CardContent>

        <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
          <Button
            size="small"
            startIcon={<Info />}
            onClick={() => setShowDetails(true)}
          >
            Details
          </Button>
          
          {canManage && (
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Tooltip title="Edit Projection">
                <IconButton size="small" onClick={handleEdit}>
                  <Edit />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete Projection">
                <IconButton size="small" onClick={() => setShowDeleteDialog(true)}>
                  <Delete />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </CardActions>
      </Card>

      {/* Details Dialog */}
      <Dialog
        open={showDetails}
        onClose={() => setShowDetails(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AttachMoney sx={{ color: 'primary.main' }} />
            <Typography variant="h6">
              Projection Details
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Main Projection */}
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Actual Projection
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main' }}>
                {formatCurrency(projection.actualProjection)}
              </Typography>
            </Box>

            {/* Projection Range */}
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Projection Range (90% - 110%)
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">Floor</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {formatCurrency(projection.projectionFloor)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Ceiling</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {formatCurrency(projection.projectionCeiling)}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Outlier Range */}
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Outlier Range
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">Minimum</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {formatCurrency(projection.outlierMinimum)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Maximum</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {formatCurrency(projection.outlierCeiling)}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Timeline Info */}
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Timeline Information
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">Days to Release</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {projection.daysUntilRelease} days
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Weeks to Release</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {projection.weeksUntilRelease} weeks
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Metadata */}
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Projection Metadata
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Projection Date:</Typography>
                  <Typography variant="body2">{formatProjectionDate(projection.projectionDate)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Created By:</Typography>
                  <Typography variant="body2">{projection.createdBy}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Created At:</Typography>
                  <Typography variant="body2">{formatProjectionDate(projection.createdAt)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Confidence:</Typography>
                  <Chip
                    label={confidenceInfo.label}
                    size="small"
                    color={confidenceInfo.color}
                    variant="outlined"
                  />
                </Box>
              </Box>
            </Box>

            {/* Notes */}
            {projection.notes && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Notes
                </Typography>
                <Typography variant="body2" sx={{ 
                  p: 2, 
                  backgroundColor: 'grey.50', 
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'grey.200'
                }}>
                  {projection.notes}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDetails(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6">
            Delete Projection
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone. The projection will be permanently deleted.
          </Alert>
          <Typography variant="body2">
            Are you sure you want to delete the projection for <strong>{formatCurrency(projection.actualProjection)}</strong> 
            created on {formatProjectionDate(projection.projectionDate)}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteDialog(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleDelete} 
            color="error" 
            variant="contained"
            disabled={deleting}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ProjectionCard;
